import { isValidPhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';

export const userCreateValidator = z.object({
  firstName: z.string().min(3, { message: 'Must be at least 3 characters' }),
  lastName: z.string().min(1, { message: 'Please enter last name' }),
  phone: z.string().refine(isValidPhoneNumber, { message: 'Invalid phone number' }),
  email: z.string().email('Must be a valid email'),
  password: z
    .string()
    .min(7, { message: 'Must be at least 7 characters' })
    .regex(/[a-z]/, { message: 'Must contain at least one lowercase letter' })
    .regex(/[A-Z]/, { message: 'Must contain at least one uppercase letter' }),
});

export const bidderCreateValidator = z.object({
  firstName: z.string().min(3, { message: 'Must be at least 3 characters' }),
  lastName: z.string().min(1, { message: 'Please enter last name' }),
  phone: z.string().refine(isValidPhoneNumber, { message: 'Invalid phone number' }),
  email: z.string().email('Must be a valid email'),
});

export const userCreateAddressValidator = z.object({
  userId: z.string().optional(),
  line1: z.string(),
  line2: z.string().optional(),
  city: z.string(),
  state: z.string(),
  postcode: z.string(),
  country: z.string(),
});

export const updateUserValidator = z.object({
  firstName: z.string(),
  lastName: z.string(),
  contactNumber: z.string(),
  country: z.string(),
  state: z.string(),
  line1: z.string(),
  line2: z.string().optional(),
  city: z.string(),
  postcode: z.string(),
});

export const updateUserPasswordValidator = z.object({
  currentPassword: z.string(),
  newPassword: z.string(),
});

export const updateUserEmailValidator = z.object({
  email: z.string(),
  password: z.string().superRefine((val, ctx) => {
    if (!/(?=.*?[a-z])(?=.*?[A-Z])/.test(val)) {
      ctx.addIssue({
        message: 'Must contain upper & lowercase characters',
        code: z.ZodIssueCode.invalid_string,
        path: ['password'],
        validation: 'regex',
      });
    }
    if (!/[!@#$%^&*(),.?":{}|<>_+=\-/]/.test(val)) {
      ctx.addIssue({
        message: 'Must contain special characters',
        code: z.ZodIssueCode.invalid_string,
        path: ['password'],
        validation: 'regex',
      });
    }
    if (val.length < 7) {
      ctx.addIssue({
        message: 'Must contain 7 or more characters',
        code: z.ZodIssueCode.too_small,
        path: ['password'],
        minimum: 7,
        inclusive: true,
        type: 'string',
      });
    }
  }),
});

export const resetPasswordValidator = z.object({
  email: z.string().email('Must be a valid email'),
  password: z.string(),
});

export const emailValidator = z.object({
  email: z.string().email('Must be a valid email'),
});

export const adminCreateUserValidator = z.object({
  firstName: z.string().min(3, { message: 'Must be at least 3 characters' }),
  lastName: z.string().min(3, { message: 'Must be at least 3 characters' }),
  email: z.string().email('Must be a valid email'),
  contactNumber: z.string(),
  country: z.string(),
  state: z.string(),
  line1: z.string(),
  line2: z.string().optional(),
  city: z.string(),
  postcode: z.string(),
});

const userListValidator = z.object({
  limit: z.number().min(1).max(100).nullish(),
  page: z.number().nullish(),
});

export type UserCreateInput = z.infer<typeof userCreateValidator>;
export type BidderCreateInput = z.infer<typeof bidderCreateValidator>;
export type UserUpdateInput = z.infer<typeof updateUserValidator>;
export type UserUpdatePasswordInput = z.infer<typeof updateUserPasswordValidator>;
export type UserUpdateEmailInput = z.infer<typeof updateUserEmailValidator>;
export type UserCreateAddressInput = z.infer<typeof userCreateAddressValidator>;
export type ResetPasswordInput = z.infer<typeof resetPasswordValidator>;
export type EmailInput = z.infer<typeof emailValidator>;
export type AdminCreateUserInput = z.infer<typeof adminCreateUserValidator>;
export type UserListInput = z.infer<typeof userListValidator>;
